import { useState, FormEvent } from "react";
import { supabase } from "@/lib/supabaseClient";
import { MultiSelectDropdown } from "@/components/ui/MultiSelectDropdown";
import { useBrand } from "@/lib/BrandContext";
import { SMILE_CENTERS, LASER_CENTERS, VERTICAL_CENTERS } from "@/lib/brandConfig";
import { CLIENT_TYPE_OPTIONS } from "@/lib/clientTypes";
import { formatParisDate, parisDateTimeToUtcIso } from "@/lib/parisDateTime";
import { prospectCaseDefaults, type ProspectContact } from "@/lib/prospectCase";

interface CreateCaseModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: (caseId: string) => void;
  prospect?: ProspectContact & { id: string; call_note: string };
}

// Activités par marque
const SMILE_ACTIVITIES = [
  "Karting",
  "Bowling",
  "Laser Game",
  "Prison Island",
  "Karaoké",
  "Billard",
  "Quizz Team",
  "Blind Test",
  "Drift Bike",
  "Trampoline",
  "Fléchettes",
  "Arcades",
];

const LASER_ACTIVITIES = [
  "Laser Game",
  "Karaoké",
  "Arcades",
];

// Extras par marque
const SMILE_EXTRAS = [
  "Offre de restauration",
  "Offre de bar",
  "Privatisation semi-complète",
  "Privatisation complète",
];

const LASER_EXTRAS = [
  "Boissons Soft",
  "Formule Apéritive",
  "Buffet Traiteur",
  "Pizzas",
  "Soirée Dansante",
  "Remise de coupe & médailles",
  "Photos & vidéos",
];

// Activités Vertical World
const VERTICAL_ACTIVITIES = [
  "Escalade",
];

// Extras Vertical World
const VERTICAL_EXTRAS: string[] = [];

function normalizeTitlePart(value: string | null | undefined): string | null {
  const trimmed = value?.trim();
  return trimmed ? trimmed : null;
}

function buildCaseTitle({
  companyName,
  clientFirstName,
  clientType,
  fallback,
}: {
  companyName: string | null;
  clientFirstName: string | null;
  clientType: string | null;
  fallback: string;
}): string {
  const parts = [companyName, clientFirstName, clientType]
    .map(normalizeTitlePart)
    .filter((part): part is string => Boolean(part));

  return parts.length > 0 ? parts.join(" - ") : fallback;
}

export function CreateCaseModal({ isOpen, onClose, onSuccess, prospect }: CreateCaseModalProps) {
  const { brand, brandConfig } = useBrand();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Options dynamiques selon la marque
  const centers = brand === 'vertical' ? VERTICAL_CENTERS : brand === 'laser' ? LASER_CENTERS : SMILE_CENTERS;
  const activitiesOptions = brand === 'vertical' ? VERTICAL_ACTIVITIES : brand === 'laser' ? LASER_ACTIVITIES : SMILE_ACTIVITIES;
  const extrasOptions = brand === 'vertical' ? VERTICAL_EXTRAS : brand === 'laser' ? LASER_EXTRAS : SMILE_EXTRAS;

  // Form state
  const [formData, setFormData] = useState({
    // Client
    client_first_name: "",
    client_last_name: "",
    client_email: "",
    client_phone: "",
    company_name: "",
    client_type: "",

    // Événement
    center: "",
    occasion: "",
    activities: [] as string[],
    extras: [] as string[],
    event_date: "",
    event_time: "",
    alt_date: "",
    participants: "",
    budget_per_person: "",
    budget: "",

    // Localisation
    address: "",
    city: "",
    postal_code: "",

    // Notes
    client_notes: "",
    internal_notes: "",
    relance_status: "",
    reminder_date: "",

    // Optionnel : titre manuel
    title: "",
    ...(prospect ? prospectCaseDefaults(prospect) : {}),
  });

  const handleChange = (field: string, value: string | string[]) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const generateLegacyAutoTitle = (): string => {
    const parts = [];
    if (formData.center) parts.push(formData.center);
    if (formData.client_last_name) parts.push(formData.client_last_name);
    else if (formData.company_name) parts.push(formData.company_name);
    else parts.push("Client");

    if (formData.event_date) {
      const parisMidnightIso = parisDateTimeToUtcIso(formData.event_date, "00:00");
      parts.push(
        parisMidnightIso
          ? formatParisDate(parisMidnightIso, {
              day: "2-digit",
              month: "2-digit",
              year: "numeric",
            })
          : formData.event_date
      );
    } else {
      parts.push("Date à définir");
    }

    return parts.join(" - ");
  };

  const generateAutoTitle = (): string => buildCaseTitle({
    companyName: formData.company_name,
    clientFirstName: formData.client_first_name,
    clientType: formData.client_type,
    fallback: generateLegacyAutoTitle(),
  });

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (submitting) return;
    setError(null);

    // Validation
    if (!formData.client_first_name.trim()) {
      setError("Le prénom du client est requis");
      return;
    }
    if (!formData.client_last_name.trim()) {
      setError("Le nom du client est requis");
      return;
    }
    if (!formData.client_email.trim()) {
      setError("L'email du client est requis");
      return;
    }
    if (!validateEmail(formData.client_email)) {
      setError("L'email du client est invalide");
      return;
    }
    if (!formData.client_type) {
      setError("Le type de client est requis");
      return;
    }

    setSubmitting(true);

    try {
      // 1. Récupérer l'utilisateur
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) throw new Error("Utilisateur non authentifié");

      // 2. Préparer les données
      const title = formData.title.trim() || generateAutoTitle();
      const source = `Ajout manuel par ${user.email}`;

      // Calcul du budget si budget_per_person et participants sont fournis
      let calculatedBudget = formData.budget ? parseFloat(formData.budget) : null;
      if (
        !calculatedBudget &&
        formData.budget_per_person &&
        formData.participants
      ) {
        calculatedBudget =
          parseFloat(formData.budget_per_person) * parseInt(formData.participants);
      }

      const caseData = {
        title,
        source,
        created_by: user.id,
        status: "Non traité",
        brand, // Marque sélectionnée

        // Client
        company_name: formData.company_name.trim() || null,
        client_type: formData.client_type || null,
        client_first_name: formData.client_first_name.trim(),
        client_last_name: formData.client_last_name.trim(),
        client_email: formData.client_email.trim(),
        client_phone: formData.client_phone.trim() || null,

        // Événement
        center: formData.center || null,
        occasion: formData.occasion.trim() || null,
        activities: formData.activities.length > 0 ? formData.activities : null,
        extras: formData.extras.length > 0 ? formData.extras : null,
        event_date: formData.event_date
          ? parisDateTimeToUtcIso(formData.event_date, formData.event_time || "00:00")
          : null,
        event_time_provided: Boolean(formData.event_date && formData.event_time),
        alt_date: formData.alt_date
          ? parisDateTimeToUtcIso(formData.alt_date, "00:00")
          : null,
        participants: formData.participants ? parseInt(formData.participants) : null,
        pax: formData.participants ? parseInt(formData.participants) : null,
        budget_per_person: formData.budget_per_person
          ? parseFloat(formData.budget_per_person)
          : null,
        budget: calculatedBudget,

        // Localisation
        address: formData.address.trim() || null,
        city: formData.city.trim() || null,
        postal_code: formData.postal_code.trim() || null,

        // Notes
        client_notes: formData.client_notes.trim() || null,
        internal_notes: formData.internal_notes.trim() || null,
        relance_status: formData.relance_status.trim() || null,
        reminder_date: formData.reminder_date
          ? parisDateTimeToUtcIso(formData.reminder_date, "00:00")
          : null,
      };

      if (prospect) {
        // Création et rattachement atomiques ; un second clic retrouve le même dossier.
        const { data: caseId, error: conversionError } = await supabase.rpc("create_case_from_prospect", {
          p_prospect_id: prospect.id,
          p_case: caseData,
        });
        if (conversionError) throw conversionError;
        if (!caseId) throw new Error("Le dossier n’a pas pu être créé.");
        onSuccess?.(caseId);
        onClose();
        return;
      }

      // 3. Insérer le case
      const { data: newCase, error: caseError } = await supabase
        .from("cases")
        .insert(caseData)
        .select("*")
        .single();

      if (caseError) throw caseError;

      console.log("✅ Case créé:", newCase.id);

      // 4. Créer le thread
      const { error: threadError } = await supabase
        .from("threads")
        .insert({ case_id: newCase.id });

      if (threadError) {
        console.error("⚠️ Erreur création thread:", threadError);
        // On continue quand même, le thread peut être créé plus tard
      } else {
        console.log("✅ Thread créé pour le case:", newCase.id);
      }

      // 5. Succès !
      // Toast géré par le parent
      onSuccess?.(newCase.id);
      onClose();
    } catch (err: any) {
      console.error("❌ Erreur création case:", err);
      setError(err.message || "Erreur lors de la création du dossier");
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div role="dialog" aria-modal="true" aria-label="Nouveau dossier" className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">
              Nouveau dossier {brand === 'vertical' ? '🧗' : brand === 'laser' ? '🔫' : '🎳'} {brandConfig.name}
            </h2>
            {formData.title.trim() ? (
              <p className="text-sm text-gray-500 mt-1">Titre : {formData.title}</p>
            ) : (
              <p className="text-sm text-gray-500 mt-1">
                Titre auto : {generateAutoTitle()}
              </p>
            )}
          </div>
          <button
            onClick={onClose}
            aria-label="Fermer la création du dossier"
            className="text-gray-400 hover:text-gray-600 transition-colors"
            disabled={submitting}
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        {/* Body */}
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto px-6 py-4">
          {prospect && <div className="mb-5 rounded-lg border border-indigo-100 bg-indigo-50 p-4 text-sm text-indigo-900">
            <p className="font-medium">Dossier de {prospect.full_name}</p>
            <p className="mt-1">Vérifiez les informations préremplies et complétez les champs manquants.</p>
            {prospect.center && <p className="mt-2">Centre demandé : {prospect.center}</p>}
            {prospect.client_type && <p>Type déclaré : {prospect.client_type}</p>}
            {prospect.participants_range && <p>Participants déclarés : {prospect.participants_range}</p>}
            {prospect.call_note && <><p className="mt-3 font-medium">Note d’appel reprise dans les notes internes</p><p className="mt-1 max-h-32 overflow-y-auto whitespace-pre-wrap break-words">{prospect.call_note}</p></>}
          </div>}
          {/* Error message */}
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
              ❌ {error}
            </div>
          )}

          {/* === CLIENT === */}
          <div className="mb-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center">
              <span className="mr-2">👤</span> Client
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Prénom <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.client_first_name}
                  onChange={(e) => handleChange("client_first_name", e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nom <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.client_last_name}
                  onChange={(e) => handleChange("client_last_name", e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  value={formData.client_email}
                  onChange={(e) => handleChange("client_email", e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Téléphone
                </label>
                <input
                  type="tel"
                  value={formData.client_phone}
                  onChange={(e) => handleChange("client_phone", e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="06 12 34 56 78"
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Société
                </label>
                <input
                  type="text"
                  value={formData.company_name}
                  onChange={(e) => handleChange("company_name", e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Type de client <span className="text-red-500">*</span>
                </label>
                <select
                  value={formData.client_type}
                  onChange={(e) => handleChange("client_type", e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  required
                >
                  <option value="">-- Sélectionner --</option>
                  {CLIENT_TYPE_OPTIONS.map((clientType) => (
                    <option key={clientType} value={clientType}>
                      {clientType}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* === ÉVÉNEMENT === */}
          <div className="mb-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center">
              <span className="mr-2">🎉</span> Événement
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Centre
                </label>
                <select
                  value={formData.center}
                  onChange={(e) => handleChange("center", e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">-- Sélectionner --</option>
                  {centers.map((center) => (
                    <option key={center} value={center}>
                      {center}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Type d'événement
                </label>
                <input
                  type="text"
                  value={formData.occasion}
                  onChange={(e) => handleChange("occasion", e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Team Building, Anniversaire..."
                />
              </div>
              <div className="md:col-span-2">
                <MultiSelectDropdown
                  label="Activités souhaitées"
                  value={formData.activities}
                  onChange={(value) => handleChange("activities", value)}
                  options={activitiesOptions}
                  placeholder="Sélectionner des activités..."
                  allowCustom={true}
                />
              </div>
              <div className="md:col-span-2">
                <MultiSelectDropdown
                  label="Extras demandés"
                  value={formData.extras}
                  onChange={(value) => handleChange("extras", value)}
                  options={extrasOptions}
                  placeholder="Sélectionner des extras..."
                  allowCustom={true}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Date de l'événement
                </label>
                <input
                  type="date"
                  value={formData.event_date}
                  onChange={(e) => handleChange("event_date", e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Heure de l'événement
                </label>
                <input
                  type="time"
                  value={formData.event_time}
                  onChange={(e) => handleChange("event_time", e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Date alternative
                </label>
                <input
                  type="date"
                  value={formData.alt_date}
                  onChange={(e) => handleChange("alt_date", e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nombre de participants
                </label>
                <input
                  type="number"
                  min="0"
                  value={formData.participants}
                  onChange={(e) => handleChange("participants", e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Budget par personne (€)
                </label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={formData.budget_per_person}
                  onChange={(e) => handleChange("budget_per_person", e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Budget total (€)
                </label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={formData.budget}
                  onChange={(e) => handleChange("budget", e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder={
                    formData.budget_per_person && formData.participants
                      ? `Auto: ${(
                          parseFloat(formData.budget_per_person) *
                          parseInt(formData.participants)
                        ).toFixed(2)}`
                      : ""
                  }
                />
              </div>
            </div>
          </div>

          {/* === LOCALISATION === */}
          <div className="mb-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center">
              <span className="mr-2">📍</span> Localisation
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Adresse
                </label>
                <input
                  type="text"
                  value={formData.address}
                  onChange={(e) => handleChange("address", e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Ville
                </label>
                <input
                  type="text"
                  value={formData.city}
                  onChange={(e) => handleChange("city", e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Code postal
                </label>
                <input
                  type="text"
                  value={formData.postal_code}
                  onChange={(e) => handleChange("postal_code", e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  maxLength={5}
                />
              </div>
            </div>
          </div>

          {/* === NOTES & SUIVI === */}
          <div className="mb-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center">
              <span className="mr-2">📝</span> Notes & Suivi
            </h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Notes du client
                </label>
                <textarea
                  value={formData.client_notes}
                  onChange={(e) => handleChange("client_notes", e.target.value)}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Précisions fournies par le client..."
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Notes internes
                </label>
                <textarea
                  value={formData.internal_notes}
                  onChange={(e) => handleChange("internal_notes", e.target.value)}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Notes internes (non visibles par le client)..."
                />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    État de relance
                  </label>
                  <input
                    type="text"
                    value={formData.relance_status}
                    onChange={(e) => handleChange("relance_status", e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="À relancer, Relancé le..."
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Date de rappel
                  </label>
                  <input
                    type="date"
                    value={formData.reminder_date}
                    onChange={(e) => handleChange("reminder_date", e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* === TITRE MANUEL (OPTIONNEL) === */}
          <div className="mb-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center">
              <span className="mr-2">🏷️</span> Titre personnalisé (optionnel)
            </h3>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Titre du dossier
              </label>
              <input
                type="text"
                value={formData.title}
                onChange={(e) => handleChange("title", e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder={`Laissez vide pour générer automatiquement : "${generateAutoTitle()}"`}
              />
              <p className="text-xs text-gray-500 mt-1">
                Si vide, sera généré automatiquement
              </p>
            </div>
          </div>
        </form>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50"
          >
            Annuler
          </button>
          <button
            type="submit"
            onClick={handleSubmit}
            disabled={submitting}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {submitting ? "Création en cours..." : "Créer le dossier"}
          </button>
        </div>
      </div>
    </div>
  );
}
